/* eslint-disable */

import callCloudRunFunction from "./cloudRunServices";
import { isLocalConversionHostname } from "./adConversionTracking";
import { readStoredAdAttribution } from "./adAttribution";
import {
  resolveFreshFbc,
  writeFbcCookie,
} from "./metaCapiParameters";
import { hasBrowserAdvertisingPrivacyOptOut } from "./paidConversionPublishing";

const isLocalhost =
  typeof window !== "undefined" &&
  isLocalConversionHostname(window.location.hostname);

const META_PIXEL_READY_EVENT = "linked-lives:meta-pixel-ready";
const META_PIXEL_ID = "922115783666464";
const META_PIXEL_SCRIPT_ID = "linked-lives-meta-pixel-script";
const META_PIXEL_QUEUE_KEY = "__LINKED_LIVES_META_PIXEL_QUEUE__";
const META_PIXEL_SENT_KEY = "__LINKED_LIVES_META_PIXEL_SENT__";
const META_PIXEL_FLUSH_KEY = "__LINKED_LIVES_FLUSH_META_PIXEL_QUEUE__";
const META_PIXEL_FLUSH_BOUND_KEY = "__LINKED_LIVES_META_PIXEL_FLUSH_BOUND__";
const META_STANDARD_PIXEL_EVENTS = new Set([
  "AddPaymentInfo",
  "AddToCart",
  "AddToWishlist",
  "CompleteRegistration",
  "Contact",
  "CustomizeProduct",
  "Donate",
  "FindLocation",
  "InitiateCheckout",
  "Lead",
  "PageView",
  "Purchase",
  "Schedule",
  "Search",
  "StartTrial",
  "SubmitApplication",
  "Subscribe",
  "ViewContent",
]);

function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]) : null;
}

function getFbcForEvent(eventSourceUrl) {
  const currentFbc = getCookie("_fbc");
  const storedAttribution = readStoredAdAttribution();
  const resolvedFbc = resolveFreshFbc({
    eventSourceUrl,
    currentFbc,
    storedFbclid: storedAttribution?.clickIds?.fbclid,
    storedFbclidCapturedAtMs:
      storedAttribution?.clickIdCapturedAtMs?.fbclid ??
      (storedAttribution?.source === "facebook"
        ? storedAttribution?.capturedAtMs
        : null),
  });

  if (resolvedFbc && resolvedFbc !== currentFbc) {
    writeFbcCookie(resolvedFbc);
  }

  return resolvedFbc;
}

function uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID)
    return crypto.randomUUID();
  return `${Date.now()}_${Math.random().toString(16).slice(2)}_${Math.random()
    .toString(16)
    .slice(2)}`;
}

function getPixelQueue() {
  if (typeof window === "undefined") return [];
  if (!Array.isArray(window[META_PIXEL_QUEUE_KEY])) {
    window[META_PIXEL_QUEUE_KEY] = [];
  }
  return window[META_PIXEL_QUEUE_KEY];
}

function getPixelSentSet() {
  if (typeof window === "undefined") return null;
  if (!(window[META_PIXEL_SENT_KEY] instanceof Set)) {
    window[META_PIXEL_SENT_KEY] = new Set();
  }
  return window[META_PIXEL_SENT_KEY];
}

function getPixelDedupeKey(eventName, eventId) {
  if (!eventName || !eventId) return "";
  return `${eventName}__${eventId}`;
}

function getMetaPixelTrackMethod(eventName) {
  return META_STANDARD_PIXEL_EVENTS.has(eventName) ? "track" : "trackCustom";
}

function cleanMetaValue(value) {
  if (value === null || value === undefined) return undefined;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed === "" ? undefined : trimmed;
  }
  if (Array.isArray(value)) {
    const cleanedArray = value
      .map(cleanMetaValue)
      .filter((item) => item !== undefined);
    return cleanedArray.length ? cleanedArray : undefined;
  }
  if (typeof value === "object") {
    const cleanedObject = cleanMetaCustomData(value);
    return Object.keys(cleanedObject).length ? cleanedObject : undefined;
  }
  return value;
}

export function cleanMetaCustomData(customData = {}) {
  const out = {};
  Object.entries(customData || {}).forEach(([key, value]) => {
    const cleanedValue = cleanMetaValue(value);
    if (cleanedValue !== undefined) out[key] = cleanedValue;
  });
  return out;
}

export function flushQueuedMetaPixelEvents() {
  if (typeof window === "undefined" || !window.fbq) return 0;

  const queue = getPixelQueue();
  const pending = queue.splice(0, queue.length);
  const sent = getPixelSentSet();
  let sentCount = 0;

  pending.forEach(({ event_name, event_id, custom_data }) => {
    if (!event_name || !event_id) return;
    const dedupeKey = getPixelDedupeKey(event_name, event_id);
    if (sent?.has(dedupeKey)) return;
    sent?.add(dedupeKey);
    window.fbq(
      getMetaPixelTrackMethod(event_name),
      event_name,
      custom_data || {},
      { eventID: event_id },
    );
    sentCount += 1;
  });

  return sentCount;
}

function ensurePixelQueueFlushHook() {
  if (typeof window === "undefined") return;

  window[META_PIXEL_FLUSH_KEY] = flushQueuedMetaPixelEvents;

  if (window[META_PIXEL_FLUSH_BOUND_KEY]) return;
  window[META_PIXEL_FLUSH_BOUND_KEY] = true;
  window.addEventListener(META_PIXEL_READY_EVENT, flushQueuedMetaPixelEvents);
}

export function configureMetaPixel() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return false;
  }

  if (
    isLocalConversionHostname(window.location.hostname) ||
    hasBrowserAdvertisingPrivacyOptOut()
  ) {
    window.__FBQ_DISABLED__ = true;
    return false;
  }

  if (window.__LINKED_LIVES_META_PIXEL_READY__) {
    flushQueuedMetaPixelEvents();
    return true;
  }

  if (!window.fbq) {
    const fbq = function () {
      fbq.callMethod
        ? fbq.callMethod.apply(fbq, arguments)
        : fbq.queue.push(arguments);
    };
    if (!window._fbq) window._fbq = fbq;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = "2.0";
    fbq.queue = [];
    window.fbq = fbq;
  }

  if (!document.getElementById(META_PIXEL_SCRIPT_ID)) {
    const script = document.createElement("script");
    script.id = META_PIXEL_SCRIPT_ID;
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    document.head.appendChild(script);
  }

  window.fbq("init", META_PIXEL_ID);
  window.__LINKED_LIVES_META_PIXEL_READY__ = true;
  ensurePixelQueueFlushHook();
  flushQueuedMetaPixelEvents();

  const readyEvent =
    typeof Event === "function"
      ? new Event(META_PIXEL_READY_EVENT)
      : document.createEvent("Event");
  if (typeof readyEvent.initEvent === "function") {
    readyEvent.initEvent(META_PIXEL_READY_EVENT, false, false);
  }
  window.dispatchEvent(readyEvent);

  return true;
}

export function firePixelStandardEvent({
  event_name,
  event_id,
  custom_data = {},
}) {
  if (typeof window === "undefined" || !event_name || !event_id) return;

  const safeCustomData = cleanMetaCustomData(custom_data);
  const dedupeKey = getPixelDedupeKey(event_name, event_id);
  const sent = getPixelSentSet();
  if (sent?.has(dedupeKey)) return;

  if (!window.fbq) {
    const queue = getPixelQueue();
    if (!queue.some((item) => item?.dedupeKey === dedupeKey)) {
      queue.push({
        dedupeKey,
        event_name,
        event_id,
        custom_data: safeCustomData,
      });
    }
    ensurePixelQueueFlushHook();
    return;
  }

  sent?.add(dedupeKey);
  window.fbq(
    getMetaPixelTrackMethod(event_name),
    event_name,
    safeCustomData,
    { eventID: event_id },
  );
}

function omitEmpty(obj) {
  const out = {};
  Object.entries(obj || {}).forEach(([k, v]) => {
    if (v === null || v === undefined) return;
    if (typeof v === "string" && v.trim() === "") return;
    if (typeof v === "object" && !Array.isArray(v)) {
      if (Object.keys(v).length === 0) return;
      out[k] = v;
      return;
    }
    out[k] = v;
  });
  return out;
}

async function postCapi(payload, { idToken = "" } = {}) {
  const cleanIdToken = String(idToken || "").trim();
  const json = await callCloudRunFunction("meta-handler", "track", payload, {
    requiresAuth: false,
    retry: {
      retries: 2,
      backoffMs: 250,
      retryOnStatuses: [429, 500, 502, 503, 504],
    },
    fetchOptions: {
      keepalive: true,
      ...(cleanIdToken
        ? {
            headers: {
              Authorization: `Bearer ${cleanIdToken}`,
              "x-ll-firebase-auth": `Bearer ${cleanIdToken}`,
            },
          }
        : {}),
    },
  });

  if (json?.ok !== true) {
    throw new Error("Meta CAPI did not accept the event");
  }

  return { ok: true, json };
}

/**
 * Provide ALL requested fields when available.
 * Unknown fields should be omitted (do not guess).
 */
export async function trackMetaEvent({
  event_name,
  event_id = uuid(),
  event_time = Math.floor(Date.now() / 1000),
  event_source_url = typeof window !== "undefined" ? window.location.href : "",
  action_source = "website",
  source_context = null,
  idToken = "",

  // Event-specific
  custom_data = {},

  // Customer info (raw; server normalizes + hashes where required)
  customer = {
    email: null,
    phone: null,
    external_id: null,
    first_name: null,
    last_name: null,
    gender: null,
    date_of_birth: null, // only full DOB if known
    city: null,
    state: null,
    zip: null,
    country: null,
  },

  // Pixel + CAPI dedupe
  send_pixel = true,
} = {}) {
  if (!event_name) throw new Error("trackMetaEvent missing event_name");

  if (hasBrowserAdvertisingPrivacyOptOut()) {
    return {
      event_id,
      server: {
        ok: true,
        skipped: true,
        reason: "browser_advertising_privacy_opt_out",
      },
    };
  }

  if (isLocalhost) {
    console.log("Meta tracking skipped", {
      event_name,
      event_id,
      reason: "localhost",
    });

    return {
      event_id,
      server: { ok: true, skipped: true },
    };
  }

  // Requested customer cookie params (no hash)
  const fbp = getCookie("_fbp");
  const fbc = getFbcForEvent(event_source_url);
  const customDataSafe = cleanMetaCustomData(custom_data);

  const payload = omitEmpty({
    // Event Parameters (required)
    event_name,
    event_time,
    event_source_url,
    action_source,
    event_id,
    source_context,

    // Customer info cookies (requested)
    fbp,
    fbc,

    // Customer info fields (requested; include only if known)
    ...omitEmpty(customer),

    // Purchase requires currency/value inside custom_data
    custom_data: customDataSafe,
  });

  if (event_name === "Purchase") {
    console.info("META_PURCHASE_TRACK_REQUEST", {
      event_name,
      event_id,
      source_context,
      value: payload?.custom_data?.value ?? null,
      currency: payload?.custom_data?.currency ?? null,
      order_id: payload?.custom_data?.order_id ?? null,
      event_source_url,
    });
  }

  if (send_pixel) {
    firePixelStandardEvent({
      event_name,
      event_id,
      custom_data: customDataSafe,
    });
  }

  const server = await postCapi(payload, { idToken });
  return { event_id, server };
}
